(() => {
  "use strict";

  const SELECTOR = "[data-mr-text-stack]";
  const instances = new WeakMap();

  class MountRigiTextStack {
    constructor(element) {
      this.element = element;
      this.items = Array.from(
        element.querySelectorAll("[data-mr-text-stack-item]")
      );

      this.context = null;
      this.timeline = null;
      this.resizeTimer = null;
      this.isDestroyed = false;

      this.boundScheduleRebuild = this.scheduleRebuild.bind(this);

      if (!this.items.length) {
        return;
      }

      this.init();
    }

    get settings() {
      return {
        startDesktop:
          this.element.dataset.startDesktop || "top 80%",

        endDesktop:
          this.element.dataset.endDesktop || "top 20%",

        startMobile:
          this.element.dataset.startMobile || "top 100%",

        endMobile:
          this.element.dataset.endMobile || "top 25%",

        stagger: Math.max(
          0,
          Number.parseFloat(this.element.dataset.stagger) || 0.18
        ),

        scrub: Math.max(
          0,
          Number.parseFloat(this.element.dataset.scrub) || 0.6
        ),

        ease: this.element.dataset.ease || "power2.out",

        startOpacity: Math.min(
          1,
          Math.max(
            0,
            Number.parseFloat(this.element.dataset.startOpacity) || 0
          )
        ),

        startScale: Math.min(
          1,
          Math.max(
            0,
            Number.parseFloat(this.element.dataset.startScale) || 0
          )
        ),

        animateMobile:
          this.element.dataset.animateMobile === "true",

        mobileBreakpoint:
          Number.parseInt(
            this.element.dataset.mobileBreakpoint,
            10
          ) || 990
      };
    }

    get isMobile() {
      return window.innerWidth < this.settings.mobileBreakpoint;
    }

    get prefersReducedMotion() {
      return window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;
    }

    async init() {
      await this.waitForFonts();

      if (this.isDestroyed) {
        return;
      }

      this.build();

      window.addEventListener(
        "resize",
        this.boundScheduleRebuild,
        { passive: true }
      );
    }

    async waitForFonts() {
      if (!document.fonts || !document.fonts.ready) {
        return;
      }

      try {
        await document.fonts.ready;
      } catch (error) {
        /*
          The animation can still initialize if the browser's
          FontFaceSet promise fails.
        */
      }
    }

    build() {
      if (
        this.isDestroyed ||
        !window.gsap ||
        !window.ScrollTrigger
      ) {
        return;
      }

      this.destroyAnimation();

      this.element.classList.remove(
        "is-reduced-motion",
        "is-static"
      );

      const shouldBeStatic =
        this.prefersReducedMotion ||
        (this.isMobile && !this.settings.animateMobile);

      if (shouldBeStatic) {
        this.element.classList.add(
          this.prefersReducedMotion
            ? "is-reduced-motion"
            : "is-static"
        );

        window.gsap.set(this.items, {
          opacity: 1,
          scaleX: 1,
          clearProps: "willChange"
        });

        this.element.classList.add("is-initialized");

        return;
      }

      const start = this.isMobile
        ? this.settings.startMobile
        : this.settings.startDesktop;

      const end = this.isMobile
        ? this.settings.endMobile
        : this.settings.endDesktop;

      this.context = window.gsap.context(() => {
        window.gsap.set(this.items, {
          opacity: this.settings.startOpacity,
          scaleX: this.settings.startScale,
          transformOrigin: "center center"
        });

        this.timeline = window.gsap.timeline({
          defaults: {
            ease: this.settings.ease
          },
          scrollTrigger: {
            trigger: this.element,
            start,
            end,
            scrub: this.settings.scrub,
            invalidateOnRefresh: true
          }
        });

        this.timeline.to(this.items, {
          opacity: 1,
          scaleX: 1,
          stagger: this.settings.stagger,
          duration: 1
        });
      }, this.element);

      this.element.classList.add("is-initialized");

      window.ScrollTrigger.refresh();
    }

    scheduleRebuild() {
      if (this.isDestroyed) {
        return;
      }

      window.clearTimeout(this.resizeTimer);

      this.resizeTimer = window.setTimeout(() => {
        this.build();
      }, 180);
    }

    destroyAnimation() {
      if (this.timeline) {
        this.timeline.scrollTrigger?.kill();
        this.timeline.kill();
        this.timeline = null;
      }

      if (this.context) {
        this.context.revert();
        this.context = null;
      }
    }

    destroy() {
      this.isDestroyed = true;

      window.clearTimeout(this.resizeTimer);

      window.removeEventListener(
        "resize",
        this.boundScheduleRebuild
      );

      this.destroyAnimation();

      this.element.classList.remove(
        "is-initialized",
        "is-reduced-motion",
        "is-static"
      );

      instances.delete(this.element);
    }
  }

  function initializeElement(element) {
    if (!element || instances.has(element)) {
      return;
    }

    const instance = new MountRigiTextStack(element);
    instances.set(element, instance);
  }

  function initializeStacks(scope = document) {
    if (scope.matches?.(SELECTOR)) {
      initializeElement(scope);
    }

    scope
      .querySelectorAll?.(SELECTOR)
      .forEach((element) => initializeElement(element));
  }

  function destroyStacks(scope) {
    if (scope.matches?.(SELECTOR)) {
      instances.get(scope)?.destroy();
    }

    scope
      .querySelectorAll?.(SELECTOR)
      .forEach((element) => {
        instances.get(element)?.destroy();
      });
  }

  function rebuildStackFromEvent(event) {
    const element = event.target.closest?.(SELECTOR);

    if (!element) {
      return;
    }

    instances.get(element)?.scheduleRebuild();
  }

  function start() {
    if (!window.gsap || !window.ScrollTrigger) {
      window.setTimeout(start, 50);
      return;
    }

    window.gsap.registerPlugin(window.ScrollTrigger);

    initializeStacks(document);

    document.addEventListener(
      "shopify:section:load",
      (event) => {
        initializeStacks(event.target);
      }
    );

    document.addEventListener(
      "shopify:section:unload",
      (event) => {
        destroyStacks(event.target);
      }
    );

    document.addEventListener(
      "shopify:section:reorder",
      () => {
        document.querySelectorAll(SELECTOR).forEach((element) => {
          instances.get(element)?.scheduleRebuild();
        });
      }
    );

    document.addEventListener(
      "shopify:block:select",
      rebuildStackFromEvent
    );

    document.addEventListener(
      "shopify:block:deselect",
      rebuildStackFromEvent
    );
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      start,
      { once: true }
    );
  } else {
    start();
  }
})();
